import { useEffect, useRef } from 'react'
import Toggle from './Toggle'
import {
  detalleDeAvisos, faltaActivarAlgo, popupPuesto, resumenDeAvisos, sePuedeEncenderElPopup,
  textoDelPopup, textoDelSonido, type PreferenciasDeAviso,
} from '../avisos'
import type { ControlDeAvisos } from '../useAvisos'

/**
 * «Avisos» en la cabecera: el número de clientes esperando y el panel con el
 * sonido, el pop-up y sus pruebas.
 *
 * Reemplaza a la campana que silenciaba con un clic. Esa campana apagaba todo
 * de una vez, no decía si el pop-up existía —no existía— y no tenía cómo
 * probar nada: la persona se enteraba de que el aviso no sonaba cuando ya se
 * le había pasado un cliente. Es el mismo panel del portal de Factoring, que
 * llegó a esto por el mismo reporte.
 *
 * El botón dice lo que falta sin abrirlo («Avisos · falta el pop-up»), en
 * ámbar: es la señal discreta que se ve en cualquier pantalla. La invitación
 * de la lista es la otra, más visible, y abre este mismo panel.
 */
export default function BotonAvisos({ control, esperando }: {
  control: ControlDeAvisos
  /** Clientes esperando respuesta: el número de la campana. */
  esperando: number
}) {
  const {
    estado, panelAbierto, setPanelAbierto, pidiendo,
    cambiarSonido, cambiarPopup, probarSonido, probarPopup,
  } = control
  const caja = useRef<HTMLDivElement>(null)

  // Cerrar al tocar afuera o con Escape: nadie vuelve a apretar el mismo botón
  // para cerrar un panel, se toca al costado.
  useEffect(() => {
    if (!panelAbierto) return
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setPanelAbierto(false)
    }
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setPanelAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', tecla)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', tecla)
    }
  }, [panelAbierto, setPanelAbierto])

  const prefs: PreferenciasDeAviso = estado ?? {
    sonido: true, popup: true, audioListo: false, permiso: 'pedible',
  }
  const apagados = !prefs.sonido && !popupPuesto(prefs)
  const falta = faltaActivarAlgo(prefs)
  const tono = apagados
    ? 'border-surface-strong bg-white text-ink-muted hover:bg-surface'
    : falta
    ? 'border-warn-border bg-warn-light text-warn hover:brightness-95'
    : 'border-brand-border bg-brand-light text-brand-hover hover:bg-brand-hoverlt'

  return (
    <div className="relative" ref={caja}>
      <button
        type="button"
        onClick={() => setPanelAbierto(!panelAbierto)}
        aria-haspopup="dialog"
        aria-expanded={panelAbierto}
        title={`${esperando} cliente(s) esperando respuesta. ${detalleDeAvisos(prefs)}`}
        className={`inline-flex items-center gap-1.5 rounded-btn border px-2.5 py-1.5 text-body font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-brand-ring ${tono}`}
      >
        <span className="relative inline-flex">
          <IconoCampana tachada={apagados} />
          {esperando > 0 && (
            <span
              aria-live="polite"
              className="absolute -right-2 -top-2 flex h-[16px] min-w-[16px] items-center justify-center rounded-full border-[1.5px] border-white bg-danger px-[3px] text-[10px] font-bold leading-none text-white"
            >
              {esperando > 9 ? '9+' : esperando}
            </span>
          )}
        </span>
        <span>{resumenDeAvisos(prefs)}</span>
        <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden className="opacity-70">
          <path d="m3 4.5 3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {panelAbierto && (
        <div
          role="dialog"
          aria-label="Avisos de mensaje nuevo"
          className="absolute right-0 top-full z-50 mt-2 w-[330px] animate-slidein rounded-panel border border-surface-border bg-white p-3.5 shadow-panel"
        >
          <p className="mb-2.5 text-meta font-bold uppercase tracking-wider text-ink-faint">
            Avisos de mensaje nuevo
          </p>

          <Fila
            icono={<IconoSonido />}
            titulo="Sonido"
            detalle={textoDelSonido(prefs)}
            control={
              <Toggle label="Sonido" checked={prefs.sonido} onChange={() => void cambiarSonido()} />
            }
          />
          <Fila
            icono={<IconoPopup />}
            titulo="Pop-up del navegador"
            detalle={textoDelPopup(prefs)}
            alerta={prefs.permiso === 'bloqueado'}
            control={
              <Toggle
                label="Pop-up del navegador"
                checked={popupPuesto(prefs)}
                // Bloqueado no se enciende desde acá —lo dice la línea de
                // abajo— y tocarlo solo cambiaría una preferencia invisible.
                disabled={pidiendo || !sePuedeEncenderElPopup(prefs.permiso)}
                onChange={() => void cambiarPopup()}
              />
            }
          />

          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-surface-faint pt-3">
            <button
              type="button"
              onClick={() => void probarSonido()}
              className="rounded-sm2 border border-surface-strong bg-white px-2 py-1.5 text-body font-bold text-ink-soft transition-colors hover:bg-surface"
            >
              Probar sonido
            </button>
            <button
              type="button"
              onClick={probarPopup}
              disabled={prefs.permiso !== 'concedido'}
              title={prefs.permiso === 'concedido'
                ? undefined
                : 'Primero hay que permitir el pop-up en el navegador.'}
              className="rounded-sm2 border border-info-border bg-info-light px-2 py-1.5 text-body font-bold text-info-ink transition-colors hover:bg-info-tint disabled:cursor-not-allowed disabled:opacity-50"
            >
              Probar pop-up
            </button>
          </div>
          {/* El navegador puede tener el permiso y Windows taparlo igual: con
              «No molestar» o con Chrome apagado en las notificaciones del
              sistema, la prueba no aparece y el portal no tiene cómo saberlo. */}
          {prefs.permiso === 'concedido' && (
            <p className="mt-2 text-meta text-ink-faint">
              ¿No apareció? En Windows: Configuración › Sistema › Notificaciones,
              con Chrome activado y «No molestar» apagado.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function Fila({ icono, titulo, detalle, control, alerta = false }: {
  icono: React.ReactNode
  titulo: string
  detalle: string
  control: React.ReactNode
  alerta?: boolean
}) {
  return (
    <div className="flex items-start gap-2.5 py-2">
      <span className="mt-0.5 shrink-0 text-ink-muted">{icono}</span>
      <div className="min-w-0 flex-1">
        <p className="text-card font-bold text-ink">{titulo}</p>
        <p className={`text-micro ${alerta ? 'text-warn' : 'text-ink-faint'}`}>{detalle}</p>
      </div>
      {control}
    </div>
  )
}

export function IconoCampana({ tachada = false, tam = 16 }: { tachada?: boolean; tam?: number }) {
  return (
    <svg width={tam} height={tam} viewBox="0 0 19 19" fill="none" aria-hidden>
      <path d="M9.5 2C6.7 2 4.5 4.2 4.5 7v3.5L3 13.5h13L14.5 10.5V7c0-2.8-2.2-5-5-5z"
            stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
      <path d="M7.5 15.5a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" />
      {tachada && (
        <path d="M3 16 16 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      )}
    </svg>
  )
}

function IconoSonido() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 2.5 4.8 5.2H2.5v5.6h2.3L8 13.5z" fill="currentColor" />
      <path d="M10.8 5.6a3.4 3.4 0 0 1 0 4.8M12.8 3.6a6.2 6.2 0 0 1 0 8.8"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function IconoPopup() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <rect x="1.75" y="2.75" width="12.5" height="10.5" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1.75 6h12.5" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}
